<template>
  <div @click="onAppClick" class="guard-app">
    <GuardOverlay :visible="overlayVisible" @dismiss="hideOverlay" />
    <Header />
    <div class="guard-body">
      <template v-if="loading">
        <CallPanelSkeleton />
        <MenuSkeleton />
      </template>
      <template v-else-if="fetchError">
        <div class="guard-error">
          <p class="guard-error__title">No se pudo cargar la información</p>
          <p class="guard-error__subtitle">
            Verifica tu conexión e intenta nuevamente
          </p>
          <button class="guard-error__retry-btn" @click="retryFetch">
            Reintentar
          </button>
        </div>
      </template>
      <template v-else-if="sublocationDetails">
        <CallPanel
          :sublocationId="$route.query.id"
          :connectionId="connectionId"
          :sublocationName="sublocationDetails.sublocation_name"
          @on-call="onCallChange"
        />
        <Menu
          :features="sublocationDetails.company_features"
          @select-feature="(feature) => onSelectFeature(feature)"
          @select-question="
            (question, feature) => onSelectQuestion(question, feature)
          "
        />
      </template>
    </div>

    <HelpModal
      :feature="selectedFeature"
      :question="selectedQuestion"
      @close="closeModal"
      @select-question="selectedQuestion = $event"
      @back="selectedQuestion = null"
    />
  </div>
</template>
<script>
import CallPanel from "../features/guard/components/CallPanel.vue";
import CallPanelSkeleton from "../features/guard/components/CallPanelSkeleton.vue";
import Header from "../features/guard/components/Header.vue";
import HelpModal from "../features/guard/components/HelpModal.vue";
import Menu from "../features/guard/components/Menu.vue";
import MenuSkeleton from "../features/guard/components/MenuSkeleton.vue";
import GuardOverlay from "../features/guard/components/GuardOverlay.vue";

const OVERLAY_TIMEOUT = 60000;
export default {
  name: "GuardDashboard",
  data() {
    return {
      sublocationDetails: null,
      loading: true,
      fetchError: false,
      selectedFeature: null,
      selectedQuestion: null,
      overlayVisible: true,
      onCall: false,
      inactivityTimer: null,
    };
  },
  components: {
    Header,
    Menu,
    MenuSkeleton,
    CallPanel,
    CallPanelSkeleton,
    HelpModal,
    GuardOverlay,
  },
  created() {
    this.connectionId = this.buildDeviceId();
    this.updateIsTouch(this.$route.query.istouch === "true");
  },
  async mounted() {
    try {
      this.sublocationDetails = await this.getSublocationDetails();
    } catch (e) {
      console.error("something went wrong getting sublocation data", e);
      this.fetchError = true;
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    if (this.inactivityTimer) clearTimeout(this.inactivityTimer);
  },
  methods: {
    async retryFetch() {
      this.fetchError = false;
      this.loading = true;
      try {
        this.sublocationDetails = await this.getSublocationDetails();
      } catch (e) {
        console.error("retry failed", e);
        this.fetchError = true;
      } finally {
        this.loading = false;
      }
    },
    onAppClick() {
      if (this.overlayVisible) return;

      this.restartInactivityTimer();
    },
    hideOverlay() {
      this.overlayVisible = false;
      this.restartInactivityTimer();
    },
    onCallChange(value) {
      this.onCall = value;
      if (value) {
        clearTimeout(this.inactivityTimer);
        this.inactivityTimer = null;
      } else {
        this.restartInactivityTimer();
      }
    },
    restartInactivityTimer() {
      clearTimeout(this.inactivityTimer);
      if (this.onCall) {
        this.inactivityTimer = null;
        return;
      }
      this.inactivityTimer = setTimeout(() => {
        if (this.onCall) return;
        this.overlayVisible = true;
      }, OVERLAY_TIMEOUT);
    },
    onSelectFeature(feature) {
      this.selectedFeature = feature;
      this.selectedQuestion = null;
    },
    onSelectQuestion(question, feature) {
      // if user selects a question we use it`s id to find to which feature it belongs, and if the feature
      // is directly selected then we only have to update it.
      if (feature) {
        this.selectedFeature = feature;
      } else {
        const feature = (this.sublocationDetails.company_features || []).find(
          (f) => (f.help_questions || []).some((q) => q.id === question.id)
        );

        this.selectedFeature = feature;
      }
      this.selectedQuestion = question;
    },
    closeModal() {
      this.selectedFeature = null;
      this.selectedQuestion = null;
    },
    async getSublocationDetails() {
      const response = await this.$http.get("digital_guard/sublocation_info", {
        params: {
          sublocation_id: this.$route.query.id,
        },
        headers: {
          "X-Device-ID": this.getDeviceId(),
        },
      });
      return response.body.data;
    },
  },
};
</script>

<style scoped>
.guard-app {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg-app, #f4f6fa);
}
.guard-body {
  flex: 1;
  display: flex;
  overflow: hidden;
  min-height: 0;
}
.guard-error {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5rem;
  padding: 2rem;
}
.guard-error__title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1b2430;
  margin: 0;
}
.guard-error__subtitle {
  font-size: 0.85rem;
  color: #5b6472;
  margin: 0 0 1rem;
}
.guard-error__retry-btn {
  background: #34b4e5;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 0.7rem 1.6rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.guard-error__retry-btn:hover {
  background: #209bcb;
}
</style>
